import React from 'react';
import { Bar } from 'react-chartjs-2';
import { LoadingSpinner, ErrorMessage, EmptyState } from '../ui';
import { useTheme } from '../../contexts/ThemeContext';
import { getChartJsOptions, getChartColors, getChartBackgroundColor } from '../../utils/themeHelpers';
import { getResponsiveXAxisTicks, isMobileChartViewport } from '../../utils/chartResponsive';
import { formatNumber } from '../../utils/formatters';
import { zeroBaselinePlugin } from '../../utils/chartPlugins';
import { CHART_GUIDES } from '../../constants/chartGuides';
import ChartControls from '../ui/ChartControls';
import { useFavoriteToggle } from '../../hooks/useFavoriteToggle';
import useChartGuide from '../../hooks/useChartGuide';
import {
  StackedPurchaseViewMode,
  activeSeries,
  formatPercentTick,
  getControllerButtonClass,
  getZeroLineGrid,
  getSortedPurchaseData,
  makeStripePattern,
  numeric,
  toMonthLabel,
  totalMonthlyInvoices,
  useCustomerPurchaseBehaviorData,
  valueLabelPlugin,
  CustomerPurchaseBehaviorChartProps,
} from './customerPurchaseBehaviorData';

const CustomerPurchaseBehaviorStackedPurchaseChart: React.FC<CustomerPurchaseBehaviorChartProps> = ({
  branchId,
  purchaseData,
  onRetry,
}) => {
  const { currentTheme } = useTheme();
  const chartColors = getChartColors(currentTheme);
  const stackedPurchaseChartRef = React.useRef<HTMLDivElement>(null);
  const { handleFavoriteToggle, isFavorited } = useFavoriteToggle();
  const { openGuide } = useChartGuide();
  const [stackedPurchaseViewMode, setStackedPurchaseViewMode] = React.useState<StackedPurchaseViewMode>('count');
  const { data: fetchedData, isLoading, error, refetch } = useCustomerPurchaseBehaviorData(branchId, {
    enabled: purchaseData === undefined,
  });
  const data = purchaseData ?? fetchedData;
  const showLoading = purchaseData === undefined && isLoading;
  const queryError = purchaseData === undefined ? error : null;
  const retry = onRetry ?? refetch;
  const isMobileChart = isMobileChartViewport();

  if (!branchId) {
    return (
      <div dir="rtl" className="card-base card-padding border border-status-warning" role="alert">
        برای دیدن این داشبورد، ابتدا شعبه را انتخاب کنید.
      </div>
    );
  }

  if (showLoading) {
    return <LoadingSpinner variant="dashboard" message="در حال بارگذاری رفتار خرید مشتریان…" />;
  }

  if (queryError) {
    return (
      <div className="card-base card-padding">
        <ErrorMessage
          message="دریافت اطلاعات رفتار خرید مشتریان با خطا مواجه شد. لطفاً دوباره تلاش کنید."
          onRetry={() => {
            retry();
          }}
        />
      </div>
    );
  }

  const sortedData = getSortedPurchaseData(data);

  if (!sortedData.length) {
    return (
      <div className="card-base card-padding">
        <EmptyState message="برای این شعبه هنوز اطلاعات رفتار خریدی ثبت نشده است" />
      </div>
    );
  }

  const labels = sortedData.map(item => toMonthLabel(item.YearMonth));
  const monthlyTotalInvoices = totalMonthlyInvoices(sortedData);
  const isDarkMode = currentTheme === 'dark';

  // tooltip سفارشی HTML: نام گروه، تعداد سفارش و سهم درصدی در ستون‌های هم‌راستا (grid)
  const getOrCreateTooltipEl = (chart: any): HTMLDivElement => {
    const parent = chart.canvas.parentNode as HTMLElement;
    let el = parent.querySelector<HTMLDivElement>('div.cpb-stacked-tooltip');
    if (!el) {
      el = document.createElement('div');
      el.className = 'cpb-stacked-tooltip';
      el.style.position = 'absolute';
      el.style.pointerEvents = 'none';
      el.style.transform = 'none';
      el.style.transition = 'opacity .12s ease';
      el.style.zIndex = '60';
      parent.appendChild(el);
    }
    return el;
  };

  const renderStackedTooltip = (context: any) => {
    const { chart, tooltip } = context;
    const el = getOrCreateTooltipEl(chart);
    if (!tooltip || tooltip.opacity === 0) {
      el.style.opacity = '0';
      return;
    }
    const dataIndex = Number(tooltip.dataPoints?.[0]?.dataIndex ?? 0);
    const monthTotal = monthlyTotalInvoices[dataIndex] || 0;

    const bg = isDarkMode ? 'rgba(17,24,39,0.97)' : 'rgba(255,255,255,0.97)';
    const titleColor = isDarkMode ? 'rgba(255,255,255,1)' : 'rgba(15,23,42,1)';
    const bodyColor = isDarkMode ? 'rgba(229,231,235,1)' : 'rgba(51,65,85,1)';
    const subColor = isDarkMode ? 'rgba(229,231,235,0.75)' : 'rgba(71,85,105,0.85)';
    const borderColor = isDarkMode ? 'rgba(99,102,241,0.4)' : 'rgba(124,58,237,0.3)';
    const dividerColor = isDarkMode ? 'rgba(148,163,184,0.25)' : 'rgba(100,116,139,0.25)';

    const rowsHtml = (tooltip.dataPoints || []).map((dp: any) => {
      const datasetIndex = Number(dp.datasetIndex || 0);
      const groupCount = numeric(sortedData[dataIndex]?.[activeSeries[datasetIndex]?.key]);
      const sharePercent = monthTotal === 0 ? 0 : (groupCount / monthTotal) * 100;
      const swatch = dp.dataset?.borderColor || chartColors[datasetIndex % chartColors.length];
      if (isMobileChart) {
        return `
          <div style="display:flex;align-items:center;justify-content:space-between;gap:8px;padding:4px 0;border-bottom:1px solid ${dividerColor};">
            <span style="display:flex;align-items:center;gap:6px;min-width:0;">
              <span style="width:10px;height:10px;border-radius:3px;flex:0 0 auto;background:${swatch};"></span>
              <span>${dp.dataset?.label ?? ''}</span>
            </span>
            <span style="font-variant-numeric:tabular-nums;white-space:nowrap;">${formatNumber(groupCount)} · ${sharePercent.toFixed(1)}%</span>
          </div>
        `;
      }
      return `
        <div style="display:flex;align-items:center;gap:6px;min-width:0;">
          <span style="width:10px;height:10px;border-radius:3px;flex:0 0 auto;background:${swatch};"></span>
          <span style="white-space:nowrap;">${dp.dataset?.label ?? ''}</span>
        </div>
        <div style="text-align:left;font-variant-numeric:tabular-nums;white-space:nowrap;">${formatNumber(groupCount)} سفارش</div>
        <div style="text-align:left;font-variant-numeric:tabular-nums;white-space:nowrap;color:${subColor};">${sharePercent.toFixed(1)}%</div>
      `;
    }).join('');

    const gridStyle = isMobileChart
      ? 'display:flex;flex-direction:column;font-size:12px;'
      : 'display:grid;grid-template-columns:1fr auto auto;column-gap:16px;row-gap:5px;align-items:center;font-size:13px;';

    el.innerHTML = `
      <div style="direction:rtl;font-family:var(--font-primary);background:${bg};color:${bodyColor};border:2px solid ${borderColor};border-radius:12px;padding:${isMobileChart ? '10px 12px' : '12px 14px'};box-shadow:0 8px 24px rgba(0,0,0,0.18);max-width:${isMobileChart ? 'calc(100vw - 2rem)' : 'none'};">
        <div style="font-weight:700;font-size:${isMobileChart ? '13px' : '14px'};color:${titleColor};margin-bottom:8px;">${labels[dataIndex] ?? ''}</div>
        <div style="${gridStyle}">
          ${rowsHtml}
        </div>
        <div style="margin-top:8px;padding-top:6px;border-top:1px solid ${dividerColor};display:flex;justify-content:space-between;gap:16px;font-size:${isMobileChart ? '12px' : '13px'};font-weight:600;color:${titleColor};">
          <span>جمع کل سفارش‌های ماه</span>
          <span style="font-variant-numeric:tabular-nums;">${formatNumber(monthTotal)} سفارش</span>
        </div>
      </div>
    `;

    // موقعیت‌یابی لبه‌آگاه: tooltip را داخل محدودهٔ نمودار نگه می‌دارد و در صورت نیاز جابه‌جا/برعکس می‌کند
    el.style.opacity = '1';
    const canvas = chart.canvas;
    const tipW = el.offsetWidth;
    const tipH = el.offsetHeight;
    const gap = 12;
    // افقی: مرکز روی caret، سپس clamp داخل عرض نمودار
    let left = canvas.offsetLeft + tooltip.caretX - tipW / 2;
    const minLeft = canvas.offsetLeft + 4;
    const maxLeft = canvas.offsetLeft + canvas.offsetWidth - tipW - 4;
    left = maxLeft >= minLeft ? Math.max(minLeft, Math.min(left, maxLeft)) : minLeft;
    // عمودی: زیر caret؛ اگر از پایین بیرون بزند، بالای caret قرار می‌گیرد
    let top = canvas.offsetTop + tooltip.caretY + gap;
    if (top + tipH > canvas.offsetTop + canvas.offsetHeight) {
      top = canvas.offsetTop + tooltip.caretY - tipH - gap;
    }
    top = Math.max(canvas.offsetTop + 4, top);
    el.style.left = left + 'px';
    el.style.top = top + 'px';
  };

  const stackedPurchaseDatasets: any[] = activeSeries.map((series, index) => ({
    type: 'bar',
    label: series.label,
    data: sortedData.map(item => numeric(item[series.key])),
    backgroundColor: (context: any) => {
      const color = getChartBackgroundColor(index, currentTheme, 0.75);
      if (!series.striped) return color;
      const chartCtx = context?.chart?.ctx;
      return chartCtx ? makeStripePattern(chartCtx, chartColors[index % chartColors.length]) : color;
    },
    borderColor: chartColors[index % chartColors.length],
    borderWidth: 1,
    borderRadius: 6,
    stack: 'purchase-bar',
  }));

  return (
    <div ref={stackedPurchaseChartRef} className="card-base card-padding animate-enter animate-delay-3">
      <ChartControls
        componentId="customer-purchase-behavior-stacked"
        componentTitle="ترکیب گروه‌های خرید در هر ماه"
        componentType="chart"
        dashboardSource="customerPurchaseBehavior"
        onInfo={() => openGuide(CHART_GUIDES.purchaseBehaviorComposition)}
        onFavorite={() => handleFavoriteToggle({
          componentId: 'customer-purchase-behavior-stacked',
          componentTitle: 'ترکیب گروه‌های خرید در هر ماه',
          dashboardSource: 'customerPurchaseBehavior',
          componentType: 'chart',
          description: 'دسته‌بندی تعداد مشتریان بر اساس تعداد خرید در ۱۲ ماه اخیر',
          props: { branchId },
        })}
        isFavorited={isFavorited('customer-purchase-behavior-stacked')}
        chartRef={stackedPurchaseChartRef}
        className="mb-4"
      />
      <h3 className="title-card">ترکیب گروه‌های خرید در هر ماه</h3>
      <div className="mb-4 border rounded-lg p-3 md:p-4 bg-slate-50/60">
        <div className="mb-2 text-xs md:text-sm font-semibold text-text-secondary text-center">
          نوع نمایش
        </div>
        <div role="group" aria-label="نوع نمایش" className="cpb-month-picker lg:flex-wrap lg:items-center lg:justify-center">
          {([
            { mode: 'count' as StackedPurchaseViewMode, label: 'نمایش تعدادی' },
            { mode: 'percent' as StackedPurchaseViewMode, label: 'نمایش درصدی' },
          ]).map((item) => {
            const isActive = stackedPurchaseViewMode === item.mode;
            return (
              <button
                key={item.mode}
                type="button"
                onClick={() => setStackedPurchaseViewMode(item.mode)}
                className={getControllerButtonClass(isActive, true)}
                aria-pressed={isActive}
              >
                {item.label}
              </button>
            );
          })}
        </div>
      </div>
      <div
        role="img"
        aria-label={`نمودار میله‌ای انباشته ${stackedPurchaseViewMode === 'percent' ? 'سهم درصدی' : 'تعداد'} مشتریان به تفکیک تعداد خرید در ۱۲ ماه اخیر`}
        className="mobile-chart-host mobile-chart-host--stacked w-full relative py-3"
      >
        <Bar
          plugins={[...(isMobileChart ? [zeroBaselinePlugin as any] : [valueLabelPlugin as any, zeroBaselinePlugin as any])]}
          data={{
            labels,
            datasets: stackedPurchaseDatasets.map((dataset, datasetIndex) => ({
              ...dataset,
              data: sortedData.map((item) => {
                const value = numeric(item[activeSeries[datasetIndex].key]);
                if (stackedPurchaseViewMode === 'count') {
                  return value;
                }
                const monthTotal = activeSeries.reduce((sum, series) => sum + numeric(item[series.key]), 0);
                if (monthTotal === 0) return 0;
                return (value / monthTotal) * 100;
              }),
              valueLabelData: sortedData.map((item) => {
                const value = numeric(item[activeSeries[datasetIndex].key]);
                if (stackedPurchaseViewMode === 'count') {
                  return value;
                }
                const monthTotal = activeSeries.reduce((sum, series) => sum + numeric(item[series.key]), 0);
                if (monthTotal === 0) return 0;
                return (value / monthTotal) * 100;
              }),
              valueLabelIsPercent: stackedPurchaseViewMode === 'percent',
              isPercent: stackedPurchaseViewMode === 'percent',
            })),
          }}
          options={{
            ...getChartJsOptions(currentTheme),
            responsive: true,
            maintainAspectRatio: false,
            interaction: {
              mode: 'index',
              intersect: false,
            },
            scales: {
              ...getChartJsOptions(currentTheme).scales,
              x: {
                ...getChartJsOptions(currentTheme).scales?.x,
                stacked: true,
                ticks: getResponsiveXAxisTicks(
                  getChartJsOptions(currentTheme).scales?.x?.ticks as Record<string, unknown> | undefined
                ),
              },
              y: {
                ...getChartJsOptions(currentTheme).scales?.y,
                grid: getZeroLineGrid(getChartJsOptions(currentTheme).scales?.y?.grid),
                stacked: true,
                ...(stackedPurchaseViewMode === 'percent'
                  ? { min: 0, max: 100 }
                  : {}),
                ticks: {
                  ...getChartJsOptions(currentTheme).scales?.y?.ticks,
                  callback: (value: any) => (
                    stackedPurchaseViewMode === 'percent'
                      ? formatPercentTick(Number(value || 0))
                      : formatNumber(Number(value || 0))
                  ),
                },
                title: {
                  display: !isMobileChart,
                  text: stackedPurchaseViewMode === 'percent' ? 'درصد ترکیب گروه‌ها' : 'تعداد سفارش',
                },
              },
            },
            layout: isMobileChart
              ? { padding: { top: 4, bottom: 4, left: 4, right: 4 } }
              : undefined,
            plugins: {
              ...getChartJsOptions(currentTheme).plugins,
              ...({
                valueLabelPlugin: {
                  enabled: !isMobileChart,
                  minPixelHeight: 16,
                  minAbsValue: 3,
                  percentDecimals: 1,
                  avoidOverlap: true,
                  labelPosition: 'center',
                  showBackground: false,
                  textColor: 'rgba(9, 14, 7, 0.96)',
                  fontSize: 12,
                },
              } as any),
              legend: {
                ...getChartJsOptions(currentTheme).plugins?.legend,
                display: !isMobileChart,
              },
              tooltip: {
                ...getChartJsOptions(currentTheme).plugins?.tooltip,
                enabled: isMobileChart,
                external: isMobileChart ? undefined : renderStackedTooltip,
              },
            },
          }}
        />
      </div>
      {isMobileChart && (
        <div className="cpb-legend-scroll" aria-label="راهنمای رنگ گروه‌های خرید">
          {activeSeries.map((series, index) => (
            <span key={series.key}>
              <span
                style={{
                  width: 10,
                  height: 10,
                  borderRadius: 3,
                  background: chartColors[index % chartColors.length],
                  display: 'inline-block',
                }}
                aria-hidden
              />
              {series.label}
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

export default CustomerPurchaseBehaviorStackedPurchaseChart;
